import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, User, Calendar, Mail, Phone, Trash2, Users } from "lucide-react";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

interface Patient {
  id: string;
  name: string;
  date_of_birth: string;
  gender: string;
  email: string;
  phone: string;
  created_at: string;
}

export default function PatientsList() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = patients.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.phone?.includes(searchQuery),
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchQuery, patients]);

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase.from("patients").select("*").order("created_at", { ascending: false });

      if (error) throw error;
      setPatients(data || []);
      setFilteredPatients(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des patients", {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const deletePatient = async (patientId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette fiche consultant ? Cette action est irréversible.")) return;
    
    try {
      const { error } = await supabase.from("patients").delete().eq("id", patientId);
      if (error) throw error;
      
      setPatients(prev => prev.filter(p => p.id !== patientId));
      setFilteredPatients(prev => prev.filter(p => p.id !== patientId));
      toast.success("Fiche consultant supprimée avec succès");
    } catch (error: any) {
      toast.error("Erreur lors de la suppression", {
        description: error.message,
      });
    }
  };

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Fiches Consultants"
        subtitle="Gérez vos fiches consultants et leur suivi"
        icon={<Users className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
        actions={
          <Button onClick={() => navigate("/patients/new")} className="gap-2 bg-gradient-primary">
            <Plus className="w-4 h-4" />
            Nouveau consultant
          </Button>
        }
      />

      <Card className="shadow-card mb-6 border-0 bg-card/80 backdrop-blur-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un consultant par nom, email ou téléphone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-border/50 focus:border-primary"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      ) : filteredPatients.length === 0 ? (
        <Card className="shadow-card border-0 bg-card/80 backdrop-blur-sm">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <User className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">
              {searchQuery ? "Aucun consultant trouvé" : "Aucun consultant enregistré"}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate("/patients/new")} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Créer votre premier consultant
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPatients.map((patient) => (
            <Card
              key={patient.id}
              className="group shadow-card hover:shadow-glow transition-all duration-300 cursor-pointer border-0 bg-card/80 backdrop-blur-sm hover:-translate-y-1"
              onClick={() => navigate(`/patients/${patient.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:from-primary/30 group-hover:to-accent/30 transition-colors">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg group-hover:text-primary transition-colors">{patient.name}</CardTitle>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs bg-secondary/10 text-secondary border-0">
                          {patient.gender}
                        </Badge>
                        {patient.date_of_birth && (
                          <Badge variant="outline" className="text-xs border-border/50">
                            {calculateAge(patient.date_of_birth)} ans
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm pt-0">
                {patient.email && (
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <div className="w-7 h-7 rounded-md bg-muted/50 flex items-center justify-center">
                      <Mail className="w-3.5 h-3.5" />
                    </div>
                    <span className="truncate">{patient.email}</span>
                  </div>
                )}
                {patient.phone && (
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <div className="w-7 h-7 rounded-md bg-muted/50 flex items-center justify-center">
                      <Phone className="w-3.5 h-3.5" />
                    </div>
                    <span>{patient.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2.5 text-muted-foreground">
                  <div className="w-7 h-7 rounded-md bg-muted/50 flex items-center justify-center">
                    <Calendar className="w-3.5 h-3.5" />
                  </div>
                  <span>Créé le {new Date(patient.created_at).toLocaleDateString("fr-FR")}</span>
                </div>
                <div className="flex justify-end pt-2 border-t border-border/30">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => deletePatient(patient.id, e)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
